"use client"

import copy from "copy-to-clipboard"
import React, { Dispatch, SetStateAction, useState } from "react"
import { Drawer as DrawerPrimitive } from "vaul"

interface Props {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
}

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)

Drawer.displayName = "Drawer"

const DrawerPortal = DrawerPrimitive.Portal

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className="fixed inset-0 z-[100] bg-[rgba(0,0,0,0.5)]"
    {...props}
  />
))

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DrawerPortal>
    <DrawerOverlay />
    <DrawerPrimitive.Content
      ref={ref}
      className="fixed inset-x-0 bottom-0 z-[110]"
      {...props}
    >
      {children}
    </DrawerPrimitive.Content>
  </DrawerPortal>
))

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title ref={ref} className="hidden" {...props}>
    share link modal
  </DrawerPrimitive.Title>
))

const ShareLinkDialog = (props: Props) => {
  const { open, setOpen } = props

  const [copied, setCopied] = useState(false)

  const handleCopyLink = () => {
    copy(window.location.href)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Drawer direction="bottom" open={open} onOpenChange={setOpen}>
      <DrawerContent
        className="h-[calc(100vh-100px)] overflow-hidden  p-0 supports-[height:100dvh]_h-[calc(100dvh-100px)]"
        showCloseButton={false}
      >
        <div className="rounded-t-[20px] bg-white p-[16px]">
          <h2 className="my-4 bg-white font-poppins text-[18px] font-bold text-primary">
            SHARE
          </h2>
          <div className="flex items-center justify-around  gap-x-2.5 bg-white p-6 pb-[30px]">
            <button
              onClick={handleCopyLink}
              type="button"
              className="flex items-center gap-x-2 px-[15px] py-[9px]"
            >
              {copied ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 16 16"
                >
                  <path
                    stroke="#2F6132"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 2 5.2 14 1 9.5"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  fill="none"
                  viewBox="0 0 18 18"
                >
                  <g
                    stroke="#2F6132"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    clipPath="url(#clip0_9059_2523)"
                  >
                    <path d="M15 6H7.5A1.5 1.5 0 0 0 6 7.5V15a1.5 1.5 0 0 0 1.5 1.5H15a1.5 1.5 0 0 0 1.5-1.5V7.5A1.5 1.5 0 0 0 15 6" />
                    <path d="M3 12c-.825 0-1.5-.675-1.5-1.5V3c0-.825.675-1.5 1.5-1.5h7.5c.825 0 1.5.675 1.5 1.5" />
                  </g>
                  <defs>
                    <clipPath id="clip0_9059_2523">
                      <path fill="#fff" d="M0 0h18v18H0z" />
                    </clipPath>
                  </defs>
                </svg>
              )}
            </button>
            <a
              href={`mailto:?subject=Check out this link&body=${encodeURIComponent(window.location.href)}`}
              className="flex items-center gap-x-2 px-[15px] py-[9px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 18 18"
              >
                <path
                  stroke="#2F6132"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M15 3H3a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 3 15h12a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 15 3"
                />
                <path
                  stroke="#2F6132"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M16.5 5.25 9.773 9.525a1.46 1.46 0 0 1-1.546 0L1.5 5.25"
                />
              </svg>
            </a>

            <a
              href={`http://www.facebook.com/sharer.php?u=${window.location.href}`}
              className="flex items-center gap-x-2 px-[15px] py-[9px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="21"
                fill="none"
                viewBox="0 0 11 21"
              >
                <path
                  fill="#2F6132"
                  d="M10.12 11.192H6.65v9.4h-3.6v-9.4H.55v-3.4h2.5v-2.9c0-2.3 1.5-4.4 4.8-4.4 1.4 0 2.4.1 2.4.1l-.13 3.2H8.05c-1.2 0-1.4.5-1.4 1.5v2.5h3.7z"
                />
              </svg>
            </a>
          </div>
        </div>
        <div
          className="absolute right-3 top-4 flex h-9 w-9 items-center justify-center rounded-[50%] bg-[#EDEEF0]"
          onClick={() => {
            setOpen(false)
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            className="lucide lucide-x text-black h-6 w-6"
            viewBox="0 0 24 24"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export default ShareLinkDialog
